import { useState, useEffect } from "react";

import { useUserContext, useAdminContext } from "../contexts";
import { CardA, ScoreCard } from "./ui/Cards";
import TrendingDrugs from "./TrendingDrugs";
import AdverseReactionPlot from "./AdverseReactionPlot";
import LocationDropdown from "./controls/LocationDropdown";
import DateDropdown from "./controls/DateDropdown";
import { useCommonDrugs, useScores, useAdverseReactionsPlotData } from "../api";

import { DateTime } from "luxon";

//Styles
import "../styles/Body.css";
import "../styles/styles.css"; // Import the CSS file

const Body = () => {
  const { user } = useUserContext();
  const { selectedCustomer } = useAdminContext();
  const customerType = user?.customerType;
  const locations = user?.locID;

  // Initialize state variables
  const now = DateTime.now().toISODate();
  const [dateRange, setDateRange] = useState(7);
  const [pastStart, setPastStart] = useState(
    DateTime.now()
      .minus({ days: dateRange - 1 })
      .toISODate()
  );
  const [selectedLocation, setSelectedLocation] = useState("All");

  // Set location name based on current location ID
  useEffect(() => {
    if (locations && locations.length > 1) {
      setSelectedLocation("All");
    } else if (locations && locations.length === 1 && user.role !== "admin") {
      setSelectedLocation(locations[0].locationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Get common drugs and adverse reactions
  const {
    data: commonDrugs,
    isFetching: isFetchingCommonDrugs,
    isError: isErrorCommonDrugs,
  } = useCommonDrugs(pastStart, "All", selectedLocation, selectedCustomer);

  const { data: scoresData, isFetching: isFetchingScores } = useScores(
    pastStart,
    now,
    DateTime.now()
      .minus({ days: dateRange * 2 })
      .toISODate(),
    pastStart,
    selectedLocation,
    selectedCustomer
  );

  const {
    data: adverseReactionsPlotData,
    isFetching: isFetchingAdverseReactionsPlotData,
  } = useAdverseReactionsPlotData(
    pastStart,
    selectedLocation,
    selectedCustomer
  );

  const handleDateRangeChange = (value) => {
    console.log("Selected date range:", value);
    const selectedDateRange = parseInt(value);
    console.log("Parsed date range:", selectedDateRange);
    setDateRange(selectedDateRange);

    const newPastStart = DateTime.now()
      .minus({ days: selectedDateRange - 1 })
      .toISODate();
    setPastStart(newPastStart);
  };

  if (isErrorCommonDrugs) {
    return <div>Error. Please try reloading the page.</div>;
  }

  return (
    <div className='full-height' style={{ marginTop: "79px" }}>
      <div
        className='px-sm-3 px-lg-5 py-3 full-height'
        // style={{ marginTop: "79px" }}
      >
        <div className='shadow card full-height'>
          <div className='card-body full-height d-flex flex-column justify-content-around'>
            <div className='d-flex justify-content-between flex-wrap'>
              <h5 className='card-title'> Testing Insights</h5>
              <div
                className='d-flex flex-wrap gap-2 align-items-center'
                style={{ marginBottom: "1rem" }}
              >
                <DateDropdown
                  dateRange={dateRange}
                  handleDateRangeChange={handleDateRangeChange}
                />

                {((user.role === "admin" &&
                  selectedCustomer?.locations &&
                  selectedCustomer?.locations?.length > 1) ||
                  (locations?.length > 1 && user.role !== "admin")) && (
                  <LocationDropdown
                    locations={
                      user.role === "admin"
                        ? selectedCustomer?.locations || []
                        : locations
                    }
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                  />
                )}
              </div>
            </div>

            <div className='d-flex flex-column justify-content-evenly full-height '>
              <div className='row justify-content-evenly d-flex '>
                <div
                  className={`${
                    customerType === "law-enforcement" ? "col-lg-6" : "col-lg-4"
                  } col-sm-12 pe-lg-2 py-1 py-lg-0 scorecard-container`}
                >
                  <ScoreCard
                    title='Samples Tested'
                    help={`Total number of samples in the current period`}
                    isFetchingScores={isFetchingScores}
                    value={scoresData?.results.currentSamples}
                    vsValue={scoresData?.results.prevSamples}
                  />
                </div>
                <div
                  className={`${
                    customerType === "law-enforcement" ? "col-lg-6" : "col-lg-4"
                  } col-sm-12 px-lg-2 py-1 py-lg-0 scorecard-container`}
                >
                  <ScoreCard
                    title='Presumption Score'
                    help={`Percentage of tested samples containing the presumed substance(s) in the current period`}
                    isFetchingScores={isFetchingScores}
                    unit='%'
                    value={scoresData?.results.currentPresScore.toFixed(2)}
                    vsValue={scoresData?.results.prevPresScore.toFixed(2)}
                  />
                </div>
                {customerType === "harm-reduction" && (
                  <div className='col-lg-4 col-sm-12 ps-lg-2 pt-1 py-lg-0 scorecard-container'>
                    <ScoreCard
                      title='Adverse Reaction Score'
                      help={`Percentage of samples with adverse reactions in the current period`}
                      isFetchingScores={isFetchingScores}
                      unit='%'
                      value={scoresData?.results.currentAdverseScore.toFixed(2)}
                      vsValue={scoresData?.results.prevAdverseScore.toFixed(2)}
                    />
                  </div>
                )}
              </div>
              <div
                className='row justify-content-evenly d-flex align-items-stretch flex-wrap'
                style={{ flexGrow: "2" }}
              >
                {/* Trending drugs */}
                <div className='col-12 col-xl-6 mt-2 plot-card-container full-height'>
                  <CardA
                    icon='fas fa-chart-pie'
                    title='Top 7 Findings'
                    className='flex-fill'
                  >
                    {isFetchingCommonDrugs ||
                    isFetchingScores ||
                    isFetchingAdverseReactionsPlotData ? (
                      <div
                        className='spinner-border'
                        role='status'
                        aria-label='Loading'
                      ></div>
                    ) : isErrorCommonDrugs ? (
                      <span>Error. Please try reloading the page.</span>
                    ) : (
                      <TrendingDrugs commonDrugs={commonDrugs.slice(0, 10)} />
                    )}
                  </CardA>
                </div>

                <div className='col-12 col-xl-6 mt-2 plot-card-container full-height '>
                  <CardA
                    icon='fas fa-chart-line'
                    title={
                      customerType === "harm-reduction"
                        ? "Adverse Reactions"
                        : "Samples Tested Per Day"
                    }
                    className='flex-fill'
                  >
                    {isFetchingAdverseReactionsPlotData ||
                    isFetchingScores ||
                    isFetchingAdverseReactionsPlotData ? (
                      <div
                        className='spinner-border'
                        role='status'
                        aria-label='Loading'
                      ></div>
                    ) : adverseReactionsPlotData.length === 0 ? (
                      <div
                        className='d-flex justify-content-center align-items-center h-100 bg-light'
                        style={{ minWidth: "90%" }}
                      >
                        No data
                      </div>
                    ) : (
                      <AdverseReactionPlot
                        period={Number(dateRange)}
                        data={adverseReactionsPlotData}
                        customerType={customerType}
                      />
                    )}
                  </CardA>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Body;
